import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  Checklist,
  Equalizer,
  Group,
  MenuBook,
  Person,
  Settings,
} from '@mui/icons-material'
import {
  Box,
  CSSObject,
  Divider,
  Drawer as MuiDrawer,
  Stack,
  styled,
  Theme,
} from '@mui/material'
import clsx from 'clsx'
import { useCurrentUser } from '~/api/useCurrentUser'
import { usePersistedStorage } from '~/hooks'
import { navigation, NavigationNode } from '~/routes/router'
import { Mozgoteka } from '../mozgoteka'
import AccountMenu from './AccountMenu'
import CollapsedTree from './CollapsedTree'
import { LinkType } from './Link.type'
import ToggleSizeButton from './ToggleSizeButton'
import Tree from './Tree'

export const drawerWidth = 240

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}))

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'visible',
  overflowY: 'clip',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'visible',
  overflowY: 'clip',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
})

const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  '& .MuiDrawer-paper': {
    position: 'fixed',
    height: '100vh',
    top: 0,
    left: 0,
    zIndex: theme.zIndex.drawer,
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': {
          ...openedMixin(theme),
          position: 'fixed',
          height: '100vh',
          top: 0,
          left: 0,
          zIndex: theme.zIndex.drawer,
        },
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': {
          ...closedMixin(theme),
          position: 'fixed',
          height: '100vh',
          top: 0,
          left: 0,
          zIndex: theme.zIndex.drawer,
        },
      },
    },
  ],
}))

const Drawer = () => {
  const { t } = useTranslation()
  const { permissions } = useCurrentUser()
  const [open, setOpen] = usePersistedStorage('drawer-open', false)

  const links = useMemo<LinkType[]>(() => {
    // Helper function to check if user has required permission for navigation type
    const hasPermissionForNavType = (navType: string): boolean => {
      if (!permissions || permissions.length === 0) return false

      switch (navType) {
        case 'knowledge':
        case 'surveys':
          return permissions.some((p) => p.name === 'surveys.read')
        case 'administration':
          return permissions.some(
            (p) =>
              p.name === 'users.read' ||
              p.name === 'groups.read' ||
              p.name === 'permissions.read'
          )
        case 'users':
          return permissions.some((p) => p.name === 'users.read')
        case 'groups':
          return permissions.some((p) => p.name === 'groups.read')
        default:
          return true // Allow access to dashboard and other general pages
      }
    }

    const createLink = (
      nav: NavigationNode | undefined
    ): LinkType | undefined => {
      if (!nav) return

      // Check permissions for this navigation item
      if (nav.type && !hasPermissionForNavType(nav.type)) {
        return undefined
      }

      let icon
      switch (nav.type) {
        case 'knowledge':
          icon = MenuBook
          break
        case 'surveys':
          icon = Equalizer
          break
        case 'scales':
          icon = Checklist
          break
        case 'administration':
          icon = Settings
          break
        case 'users':
          icon = Person
          break
        case 'groups':
          icon = Group
          break
      }
      const link = {
        id: nav.id,
        title: t(`pages.${nav.type}.title`),
        icon,
        path: nav.path,
      } as LinkType
      const children = nav.children?.map(createLink)
      if (children) {
        link.children = children.filter(
          (c): c is LinkType => c !== undefined
        ) as LinkType[]
      }
      return link
    }
    return navigation
      .map(createLink)
      .filter((c): c is LinkType => c !== undefined)
  }, [t, permissions])

  return (
    <StyledDrawer
      variant="permanent"
      open={open}
      PaperProps={{ elevation: 1 }}
      sx={{ filter: 'drop-shadow(1px 1px 4px rgba(0,0,0,.15))' }}
    >
      <DrawerHeader sx={open ? undefined : { justifyContent: 'center' }}>
        <Link className="block" to="/">
          <Mozgoteka
            className={clsx('w-full', open ? 'px-3 py-2' : 'max-h-[48px] p-1')}
            safe={false}
            signet={!open}
          />
        </Link>
      </DrawerHeader>
      <Divider />
      <Box className="grow overflow-x-hidden relative">
        <Box className="absolute w-full">
          {open ? (
            <Box sx={{ py: 1.5 }}>
              <Tree items={links} />
            </Box>
          ) : (
            <CollapsedTree items={links} />
          )}
        </Box>
      </Box>
      <Divider />
      <Stack
        direction="row"
        padding={1}
        sx={{ position: 'relative', overflowX: 'visible' }}
      >
        <ToggleSizeButton open={open} setOpen={setOpen} />
        <AccountMenu />
      </Stack>
    </StyledDrawer>
  )
}

Drawer.width = drawerWidth

export default Drawer
